import { useState, useEffect, useRef } from 'react';

const useSearchNavigation = (
  conversations,
  {
    searchTerm,
    highlightedResult,
    setSearchTerm,
    setHighlightedResult,
    setFilteredConversations
  }
) => {
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const cardRefs = useRef({});
  const isCleaningRef = useRef(false);

  const filteredConversations = conversations?.filter(conversation =>
    conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  useEffect(() => {
    if (!searchTerm || !conversations || !Array.isArray(conversations)) return;

    const timeoutId = setTimeout(() => {
      if (JSON.stringify(conversations) !== JSON.stringify(filteredConversations)) {
        setFilteredConversations(conversations);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, setFilteredConversations]);
  
  useEffect(() => {
    if (isCleaningRef.current) return;

    if (highlightedResult >= 0) {
      setCurrentResultIndex(highlightedResult);
    }

    if (!searchTerm) {
      setCurrentResultIndex(-1);
      setHighlightedResult(-1);
      cardRefs.current = {};
      return;
    }

    const timeoutId = setTimeout(() => {
      updateCardRefs();

      if (filteredConversations.length > 0 && currentResultIndex === -1) {
        setCurrentResultIndex(0);
        setHighlightedResult(0);
      }

      if (highlightedResult >= 0) {
        scrollToHighlightedCard();
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, highlightedResult, filteredConversations.length, currentResultIndex]);

  const updateCardRefs = () => {
    if (!searchTerm) return;

    const cards = document.querySelectorAll('.searchable-card');
    const newRefs = {};

    const maxCards = Math.min(cards.length, 50);

    for (let i = 0; i < maxCards; i++) {
      const card = cards[i];
      const index = card.getAttribute('data-index');
      if (index && index !== '-1') {
        newRefs[`card-${index}`] = card;
      }
    }
    
    cardRefs.current = newRefs;
  };

  const scrollToHighlightedCard = () => {
    const hasHighlightedCardRef = 
      highlightedResult >= 0 && 
      cardRefs.current[`card-${highlightedResult}`];
      
    if (!hasHighlightedCardRef) return;
    
    const cardElement = cardRefs.current[`card-${highlightedResult}`];
    const container = document.querySelector('.history-container');
    
    if (!container) return;

    const currentHighlighted = container.querySelector('.searchable-card.highlighted');
    if (currentHighlighted) {
      currentHighlighted.classList.remove('highlighted');
    }
    
    cardElement.classList.add('highlighted');

    const cardOffset = cardElement.offsetTop;
    const containerHeight = container.clientHeight;
    const cardHeight = cardElement.offsetHeight;
    const scrollPosition = cardOffset - (containerHeight / 2) + (cardHeight / 2);

    container.scrollTop = scrollPosition;
  };

  /**
   * Highlights a search term in a given text
   * @param {string} text - The original text
   * @param {string} term - The term to highlight
   * @returns {string} - HTML with the highlighted term
   */
  const highlightMatchedText = (text, term) => {
    if (!term || term.trim() === '') return text;

    try {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedTerm})`, 'gi');

      return text.replace(regex, '<strong>$1</strong>');
    } catch (error) {
      return text;
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleClearSearch = () => {
    isCleaningRef.current = true;

    setSearchTerm('');
    setHighlightedResult(-1);

    setCurrentResultIndex(-1);

    cardRefs.current = {};

    const cards = document.querySelectorAll('.searchable-card');
    cards.forEach(card => card.classList.remove('highlighted'));

    setTimeout(() => {
      isCleaningRef.current = false;
    }, 150);
  };

  const navigateNext = () => {
    if (filteredConversations.length === 0) return;
    
    const nextIndex = currentResultIndex < filteredConversations.length - 1 
      ? currentResultIndex + 1 
      : 0;
    
    setCurrentResultIndex(nextIndex);
    setHighlightedResult(nextIndex);
  };

  const navigatePrevious = () => {
    if (filteredConversations.length === 0) return;
    
    let prevIndex;
    if (currentResultIndex > 0) {
      prevIndex = currentResultIndex - 1;
    } else if (currentResultIndex === 0) {
      prevIndex = filteredConversations.length - 1;
    } else {
      prevIndex = filteredConversations.length - 1;
    }
    
    setCurrentResultIndex(prevIndex);
    setHighlightedResult(prevIndex);
  };

  return {
    currentResultIndex: currentResultIndex >= 0 ? currentResultIndex + 1 : 0,
    filteredConversations,
    handleSearch,
    handleClearSearch,
    navigateNext,
    navigatePrevious,
    highlightMatchedText,
    searchTerm
  };
};

export default useSearchNavigation; 